import type { WeekCalendarProps } from "../types";
import type { MobileCalendarViewProps } from "../components/mobile/MobileCalendarView";
import type { DesktopCalendarViewProps } from "../components/DesktopCalendarView";
import { getToday } from "../../../shared/types/date-helpers";
import { useNowIndicator } from "./use-now-indicator";
import { useCalendarNavigation } from "./use-calendar-navigation";
import { useWeekCalendarState } from "./use-week-calendar-state";
import { useEventIndexing } from "./use-event-indexing";
import { useEventHandlers } from "./use-event-handlers";
import { useRecurrenceHandlers } from "./use-recurrence-handlers";
import { useKeyboardNavigation } from "./use-keyboard-navigation";
import { useEditingEventDelete } from "../components/dialogs/useEditingEventDelete";
import { useDisplayDays } from "./use-display-days";
import { useCalendarViewProps } from "./use-calendar-view-props";
import { getClipboardEvent } from "../stores/clipboard-store";

interface CalendarViewProps {
  mobileProps: MobileCalendarViewProps;
  desktopProps: DesktopCalendarViewProps;
}

export function useWeekCalendarSetup(
  props: WeekCalendarProps,
): CalendarViewProps {
  const today = getToday();
  const now = useNowIndicator();
  const nav = useCalendarNavigation(today);
  const {
    showAddForm,
    setShowAddForm,
    formStartTime,
    setFormStartTime,
    formEndTime,
    setFormEndTime,
    formAnchor,
    setFormAnchor,
    editingEvent,
    setEditingEvent,
    showFullEditForm,
    setShowFullEditForm,
    pendingRecurrence,
    setPendingRecurrence,
    pendingResync,
    setPendingResync,
    recurrenceToast,
    setRecurrenceToast,
  } = useWeekCalendarState();

  const {
    eventsByDate,
    tasksByDate,
    categoryDotsForDate,
    selectedEvents,
    selectedTasks,
  } = useEventIndexing(props.events, props.tasks, nav.selectedDate);

  const {
    handleEventClick,
    handleEventLongPress,
    handleSlotClick,
    handleMobileSlotClick,
    closeAddForm,
    handleOpenAdd,
    handleDuplicate,
    handleUpdateEvent,
  } = useEventHandlers({
    events: props.events,
    nav,
    setShowAddForm,
    setFormStartTime,
    setFormEndTime,
    setFormAnchor,
    setEditingEvent,
    setShowFullEditForm,
    selectedEvents,
    setPendingRecurrence,
    setPendingResync,
    onAddEvent: props.onAddEvent,
    onAddEventFull: props.onAddEventFull,
    onDeleteEvent: props.onDeleteEvent,
    onAddRecurrenceException: props.onAddRecurrenceException,
    onResyncOccurrence: props.onResyncOccurrence,
    onResyncAllOccurrences: props.onResyncAllOccurrences,
    onUpdateEvent: props.onUpdateEvent,
  });

  const { handleDelete: handleDeleteEditingEvent } = useEditingEventDelete({
    events: props.events,
    onDeleteEvent: props.onDeleteEvent,
    onSetEditingEvent: setEditingEvent,
    onSetRecurrenceToast: setRecurrenceToast,
    onAddRecurrenceException: props.onAddRecurrenceException,
    onUndoEvent: props.onUndoEvent,
  });

  const { handleRecurrenceChoice, handleResyncChoice } = useRecurrenceHandlers({
    onDeleteEvent: props.onDeleteEvent,
    onAddRecurrenceException: props.onAddRecurrenceException,
    onUpdateEvent: props.onUpdateEvent,
    onAddEventFull: props.onAddEventFull,
    onResyncOccurrence: props.onResyncOccurrence,
    onResyncAllOccurrences: props.onResyncAllOccurrences,
    setPendingRecurrence,
    setPendingResync,
    setEditingEvent,
  });

  useKeyboardNavigation({
    nav,
    setShowAddForm,
    setShowFullEditForm,
    setEditingEvent,
    hasEditingEvent: editingEvent !== null,
    editingEvent,
    onDeleteEditingEvent: editingEvent
      ? () => handleDeleteEditingEvent(editingEvent.id)
      : undefined,
    onPasteEvent: props.onAddEventFull
      ? (date: string, startTime?: string) => {
          const clip = getClipboardEvent();
          if (!clip || !props.onAddEventFull) return;
          const resolvedStart = startTime ?? clip.startTime;
          // Compute duration to shift endTime accordingly
          const [sh, sm] = clip.startTime.split(":").map(Number);
          const [eh, em] = clip.endTime.split(":").map(Number);
          const durationMin = ((eh ?? 0) * 60 + (em ?? 0)) - ((sh ?? 0) * 60 + (sm ?? 0));
          const [rh, rm] = resolvedStart.split(":").map(Number);
          const endMin = Math.min((rh ?? 0) * 60 + (rm ?? 0) + durationMin, 24 * 60);
          const resolvedEnd = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
          props.onAddEventFull({
            title: `${clip.title} (copie)`,
            category: clip.category,
            date,
            startTime: resolvedStart,
            endTime: resolvedEnd,
            description: clip.description,
            notes: clip.notes,
            tags: [...clip.tags],
            recurrence: clip.recurrence,
            reminderMinutes: clip.reminderMinutes,
            reminderTime: clip.reminderTime ?? null,
            reminderType: clip.reminderType ?? null,
          });
        }
      : undefined,
  });

  const displayDays = useDisplayDays(nav);
  const { mobileProps, desktopProps } = useCalendarViewProps({
    nav,
    today,
    displayDays,
    now,
    events: props.events,
    tasks: props.tasks,
    eventsByDate,
    tasksByDate,
    categoryDotsForDate,
    selectedEvents,
    selectedTasks,
    editingEvent,
    showAddForm,
    showFullEditForm,
    formStartTime,
    formEndTime,
    formAnchor,
    activeCategory: props.activeCategory,
    pendingRecurrence,
    pendingResync,
    recurrenceToast,
    onHandleEventClick: handleEventClick,
    onHandleEventLongPress: handleEventLongPress,
    onHandleSlotClick: handleSlotClick,
    onHandleMobileSlotClick: handleMobileSlotClick,
    onHandleUpdateEvent: handleUpdateEvent,
    onHandleOpenAdd: handleOpenAdd,
    onSetEditingEvent: setEditingEvent,
    onAddEvent: props.onAddEvent,
    onAddEventFull: props.onAddEventFull,
    onDeleteEvent: props.onDeleteEvent,
    onUpdateEvent: props.onUpdateEvent,
    onSetShowAddForm: setShowAddForm,
    onSetFormStartTime: setFormStartTime,
    onSetFormEndTime: setFormEndTime,
    onSetShowFullEditForm: setShowFullEditForm,
    onSetPendingResync: setPendingResync,
    onSetRecurrenceToast: setRecurrenceToast,
    onHandleDuplicate: handleDuplicate,
    onAddRecurrenceException: props.onAddRecurrenceException,
    onUndoEvent: props.onUndoEvent,
    onCloseAddForm: closeAddForm,
    onHandleRecurrenceChoice: (action) => {
      if (pendingRecurrence) handleRecurrenceChoice(action, pendingRecurrence);
    },
    onHandleResyncChoice: (action) => {
      if (pendingResync) handleResyncChoice(action, pendingResync);
    },
    onSetPendingRecurrence: setPendingRecurrence,
    readOnly: props.readOnly,
    onToggleReadOnly: props.onToggleReadOnly,
  });

  return { mobileProps, desktopProps };
}
